import { useState } from "react";
import { toast } from "sonner";
import { api, uploadMedia } from "../lib/api";

const moveItem = (list, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const updated = [...list];
  [updated[index], updated[target]] = [updated[target], updated[index]];
  return updated;
};

const withKeys = (items) => items.map((item) => ({ ...item, _key: item._key ?? crypto.randomUUID() }));
const stripKeys = (items) => items.map(({ _key, ...rest }) => rest);

/** All state/handlers for the Admin > Páginas SEO editor form: fields, highlights, FAQ, save, preview. */
export const useSeoPageForm = (page, slug, onSaved) => {
  const [form, setForm] = useState(() => ({
    ...page,
    highlights: withKeys(page.highlights),
    faq: withKeys(page.faq),
  }));
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const { url } = await uploadMedia(file, "image");
      update("hero_image", url);
      toast.success("Fotografía principal subida");
    } catch {
      toast.error("Error al subir la fotografía");
    } finally {
      setUploadingHero(false);
      e.target.value = "";
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const { url } = await uploadMedia(file, "video");
      update("video_url", url);
      toast.success("Video subido");
    } catch {
      toast.error("Error al subir el video (máx. 50MB, formatos mp4/mov/webm)");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const removeVideo = () => update("video_url", "");

  const updateHighlight = (idx, item) => setForm((f) => {
    const highlights = [...f.highlights];
    highlights[idx] = item;
    return { ...f, highlights };
  });
  const addHighlight = () => setForm((f) => ({ ...f, highlights: [...f.highlights, { _key: crypto.randomUUID(), title: "", description: "" }] }));
  const removeHighlight = (idx) => setForm((f) => ({ ...f, highlights: f.highlights.filter((_, i) => i !== idx) }));
  const moveHighlight = (idx, dir) => setForm((f) => ({ ...f, highlights: moveItem(f.highlights, idx, dir) }));

  const updateFaq = (idx, item) => setForm((f) => {
    const faq = [...f.faq];
    faq[idx] = item;
    return { ...f, faq };
  });
  const addFaq = () => setForm((f) => ({ ...f, faq: [...f.faq, { _key: crypto.randomUUID(), question: "", answer: "" }] }));
  const removeFaq = (idx) => setForm((f) => ({ ...f, faq: f.faq.filter((_, i) => i !== idx) }));
  const moveFaq = (idx, dir) => setForm((f) => ({ ...f, faq: moveItem(f.faq, idx, dir) }));

  const save = async () => {
    setSaving(true);
    const payload = {
      eyebrow: form.eyebrow, h1: form.h1, intro: form.intro,
      highlights_title: form.highlights_title, faq_title: form.faq_title,
      quote_button_text: form.quote_button_text, whatsapp_button_text: form.whatsapp_button_text,
      hero_image: form.hero_image, secondary_images: form.secondary_images,
      video_url: form.video_url,
      highlights: stripKeys(form.highlights), faq: stripKeys(form.faq),
      seo_title: form.seo_title, seo_description: form.seo_description,
    };
    try {
      const { data } = await api.put(`/admin/seo-pages/${form.page_id}`, payload);
      setForm({ ...data, highlights: withKeys(data.highlights), faq: withKeys(data.faq) });
      toast.success("Página actualizada. Los cambios ya están visibles en el sitio.");
      onSaved?.(data);
    } catch {
      toast.error("Error al guardar la página");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    try {
      sessionStorage.setItem("twp_seo_preview", JSON.stringify({ ...form, slug }));
    } catch {
      // sessionStorage unavailable — preview will just show stale/no data, non-critical
    }
    setPreviewOpen(true);
  };

  return {
    form, update, saving, uploadingHero, handleHeroUpload,
    uploadingVideo, handleVideoUpload, removeVideo,
    updateHighlight, addHighlight, removeHighlight, moveHighlight,
    updateFaq, addFaq, removeFaq, moveFaq,
    save, previewOpen, setPreviewOpen, openPreview,
  };
};
